import { useEffect, useMemo, useState } from "react";
import {
  MiBox,
  MiButton,
  MiList,
  MiMenu,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { ISeriesGroupResponse, ISeriesResponse } from "@miplatform/types";
import { mdiPencilOutline, mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { ConfigPageLayout } from "../../../../Config/ConfigPageLayout";
import { SectionInfo } from "../../../components/SectionInfo/SectionInfo";
import { AddSeriesGroupModal } from "./AddSeriesGroupModal/AddSeriesGroupModal";
import { AddSeriesModal } from "./AddSeriesModal/AddSeriesModal";
import {
  UNASSIGNED_GROUP_NAME,
  useSeriesDetailViewModel,
} from "./useSeriesDetailViewModel";

export function SeriesDetail() {
  const { params } = useRouter();
  const accountId = params.id as string;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const [openSeriesModal, setOpenSeriesModal] = useState(false);
  const [openSeriesGroupModal, setOpenSeriesGroupModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openDeleteGroupModal, setOpenDeleteGroupModal] = useState(false);

  const theme = useTheme();

  const {
    isLoading,
    accountDetails,
    seriesGroups,
    series,
    refresh,
    setRefresh,
    getAccountDetails,
    getAccountSeriesGroups,
    getAccountSeries,
    handleDeleteSeries,
    handleDeleteSeriesGroup,
    selectedSeries,
    setSelectedSeries,
    selectedSeriesGroup,
    setSelectedSeriesGroup,
  } = useSeriesDetailViewModel({ accountId });

  useEffect(() => {
    getAccountDetails();
  }, [accountId]);

  useEffect(() => {
    getAccountSeriesGroups();
  }, [accountId, refresh]);

  const seriesGroupNameById = useMemo(
    () => new Map(seriesGroups.map((group) => [group.id, group.name])),
    [seriesGroups]
  );

  const seriesListData = useMemo(() => {
    const matchingSeries = debouncedSearch
      ? series.filter((seriesItem) =>
          seriesItem.name?.toLowerCase().includes(debouncedSearch.toLowerCase())
        )
      : series;

    const emptyGroupPlaceholders = seriesGroups
      .filter((group) => !group.series?.length)
      .map(
        (group) =>
          ({
            id: null,
            seriesGroupId: group.id,
            accountId,
          } as unknown as ISeriesResponse)
      );

    const hasUnassignedSeries = series.some(
      (seriesItem) => !seriesItem.seriesGroupId
    );
    const unassignedPlaceholder = hasUnassignedSeries
      ? []
      : [
          {
            id: null,
            seriesGroupId: null,
            accountId,
          } as unknown as ISeriesResponse,
        ];

    return [
      ...matchingSeries,
      ...emptyGroupPlaceholders,
      ...unassignedPlaceholder,
    ];
  }, [series, seriesGroups, accountId, debouncedSearch]);

  const handleOpenDeleteModal = (seriesToDelete: ISeriesResponse) => {
    setSelectedSeries(seriesToDelete);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    if (selectedSeries) {
      handleDeleteSeries(selectedSeries);
    }
    setOpenDeleteModal(false);
  };

  const handleOpenGroupDeleteModal = (seriesGroup: ISeriesGroupResponse) => {
    setSelectedSeriesGroup(seriesGroup);
    setOpenDeleteGroupModal(true);
  };

  const handleConfirmGroupDeleteModal = () => {
    if (selectedSeriesGroup) {
      handleDeleteSeriesGroup(selectedSeriesGroup);
    }
    setOpenDeleteGroupModal(false);
  };

  const handleSeriesCardClick = (seriesItem: ISeriesResponse) => {
    setSelectedSeries(seriesItem);
    setOpenSeriesModal(true);
  };

  const renderItemActions = (seriesItem: ISeriesResponse) => (
    <Box onClick={(e) => e.stopPropagation()}>
      <MiMenu
        data={[
          {
            label: BUTTON_LABELS.EDIT,
            icon: mdiPencilOutline,
            action: () => {
              setSelectedSeries(seriesItem);
              setOpenSeriesModal(true);
            },
          },
          {
            label: BUTTON_LABELS.DELETE,
            icon: mdiTrashCanOutline,
            action: () => handleOpenDeleteModal(seriesItem),
            color: theme.palette.red.main,
          },
        ]}
      />
    </Box>
  );

  const renderGroupActions = (seriesItem: ISeriesResponse) => {
    const groupId = seriesItem.seriesGroupId;
    if (!groupId) {
      return null;
    }

    const seriesGroup = seriesGroups.find((group) => group.id === groupId);
    if (!seriesGroup) {
      return null;
    }

    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu
          data={[
            {
              label: BUTTON_LABELS.EDIT,
              icon: mdiPencilOutline,
              action: () => {
                setSelectedSeriesGroup(seriesGroup);
                setOpenSeriesGroupModal(true);
              },
            },
            {
              label: BUTTON_LABELS.DELETE,
              icon: mdiTrashCanOutline,
              action: () => handleOpenGroupDeleteModal(seriesGroup),
              color: theme.palette.red.main,
            },
          ]}
        />
      </Box>
    );
  };

  const sections = [
    {
      fieldset: [
        {
          renderCell: (seriesItem: ISeriesResponse) => (
            <SectionInfo
              data={{
                name: seriesItem.name,
                modifiedOn: seriesItem.modifiedOn,
              }}
            />
          ),
        },
      ],
    },
  ];

  const seriesGroupOptions = useMemo(
    () => seriesGroups.map((group) => ({ id: group.id, name: group.name })),
    [seriesGroups]
  );

  const renderTitle = () => (
    <Typography variant={TYPOGRAPHY_VARIANTS.HEADER as any}>
      Series:{" "}
      <Typography
        variant={TYPOGRAPHY_VARIANTS.HEADER as any}
        color="primary"
        component="span"
      >
        {accountDetails?.name}
      </Typography>
    </Typography>
  );

  return (
    <ConfigPageLayout
      renderTitle={renderTitle}
      loading={isLoading}
      search={search}
      onHandleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
        setSearch(e.target.value)
      }
      titleActionComponent={
        <Box display="flex" gap={2}>
          <MiButton
            title="Add Series Group"
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{ path: mdiPlus }}
            onClick={() => setOpenSeriesGroupModal(true)}
            data-testid="add-series-group-btn"
          />
          <MiButton
            title="Add Series"
            variant={BUTTON_VARIANTS.CONTAINED}
            startIcon={{ path: mdiPlus }}
            onClick={() => setOpenSeriesModal(true)}
            data-testid="add-series-btn"
          />
        </Box>
      }
    >
      <MiBox>
        <MiList
          viewType="card"
          data={seriesListData}
          loading={isLoading}
          dataProvider={getAccountSeries}
          refresh={`${accountId}:${refresh}`}
          cardProps={{
            sections,
            renderItemActions,
            renderGroupActions,
            onClick: handleSeriesCardClick,
          }}
          groupedMode
          groupedCriteria={(seriesItem: ISeriesResponse) =>
            seriesItem.seriesGroupId
              ? seriesGroupNameById.get(seriesItem.seriesGroupId) ??
                UNASSIGNED_GROUP_NAME
              : UNASSIGNED_GROUP_NAME
          }
          pinGroupKeyToTop={UNASSIGNED_GROUP_NAME}
          showGroupCount
          showEmptyGroups
          paginationMode={PAGINATION.CLIENT}
          emptyListProps={{ text: "No Series found" }}
          data-testid="series-list"
        />
      </MiBox>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal || openDeleteGroupModal}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        titleIcon={{
          path: mdiTrashCanOutline,
          color: theme.palette.red.main,
        }}
        handleNegative={() => {
          if (openDeleteModal) {
            setOpenDeleteModal(false);
            setSelectedSeries(null);
          } else {
            setOpenDeleteGroupModal(false);
            setSelectedSeriesGroup(null);
          }
        }}
        handlePositive={() =>
          openDeleteModal
            ? handleConfirmDeleteModal()
            : handleConfirmGroupDeleteModal()
        }
        description={`You are about to delete ${
          openDeleteModal
            ? `${selectedSeries?.name} series`
            : `${selectedSeriesGroup?.name} series group`
        }. Are you sure you would like to continue?`}
      />
      <AddSeriesModal
        open={openSeriesModal}
        closeModal={() => setOpenSeriesModal(false)}
        selectedSeries={selectedSeries}
        setSelectedSeries={setSelectedSeries}
        setRefresh={setRefresh}
        accountId={accountId}
        seriesGroups={seriesGroupOptions}
      />
      <AddSeriesGroupModal
        open={openSeriesGroupModal}
        closeModal={() => setOpenSeriesGroupModal(false)}
        selectedSeriesGroup={selectedSeriesGroup}
        setSelectedSeriesGroup={setSelectedSeriesGroup}
        setRefresh={setRefresh}
        accountId={accountId}
      />
    </ConfigPageLayout>
  );
}
